import { ChatCompletionRequestMessageRole } from '@server/ai';
import { Fragment, useCallback, useEffect, useState, useMemo, ReactNode, useRef } from 'react';
import { AiChatAssistantMessageBox } from './message-atoms/AiChatAssistantMessageBox';
import { AiChatAssistantMessageBoxSimple } from './message-atoms/AiChatAssistantMessageBoxSimple';
import { AiChatUserMessageBox } from './message-atoms/AiChatUserMessageBox';
import { AiChatMessage } from './aiChatTypes';
import { getTextContent, buildArtifactVersionMap } from './aiChatHelpers';
import AiChatPaywallBlockMessage from './AiChatPaywallBlockMessage';
import { Box, Stack } from '@mui/material';
import AiChatInitialFetchMessage from './AiChatInitialFetchMessage';
import { highlightMatchInMarkdown } from '../../../lib/highlightMatchInMarkdown';
import usePrevious from '../../../hooks/usePrevious';
import { DataTableVariant } from '../table/TableTypes';
import { CenteredCircularProgress } from '../CenteredCircularProgress';
import { colors } from '@sweep-io/sweep-design';

const getElementPositionRelativeToScreenViewport = (
  containerRef: React.MutableRefObject<HTMLDivElement | undefined>,
) => {
  const rect = containerRef.current?.getBoundingClientRect();
  if (!rect) return undefined;

  return {
    topFromTop: rect.top,
    bottomFromBottom: document.documentElement.clientHeight - (rect.top + rect.height),
  };
};

interface AiChatMessagesProps {
  containerRef: React.MutableRefObject<HTMLDivElement | undefined>;
  messages: AiChatMessage[];
  searchTermToHighlight?: string;
  renderInitialFetchMessage: boolean;
  renderChatPaywallBlockMessage: boolean;
  renderLoader: boolean;
  errorMsg?: string;
  activeChatId?: string;
  onTextSelection?: (text: string | null) => void;
  isStreamingResponse?: boolean;
  chatName?: string;
  dataTableVariant?: DataTableVariant;
  spacerFix?: number; //this can be used to adjust the spacer height (used to render the user's message on the container's top)
  onAnimationProgressPersistence?: (progress: {
    messageCharIndex: number;
    reasoningChunkIndex: number;
  }) => void;
  crmOrgId: string;
  hideActionButtons?: boolean;
  lastUserMessageAction?: ReactNode;
}

const GAP_BETWEEN_MESSAGES = 16;
const VISIBLE_GAPS_COUNTS = 2; //one between user mgs and assistant msg (or loader), one between assistant msg and spacer
const LOADER_HEIGHT = 24;

export const AiChatMessages = ({
  containerRef,
  messages,
  searchTermToHighlight,
  renderInitialFetchMessage,
  renderChatPaywallBlockMessage,
  renderLoader,
  errorMsg,
  activeChatId,
  onTextSelection,
  isStreamingResponse = false,
  chatName,
  dataTableVariant = DataTableVariant.small,
  spacerFix,
  onAnimationProgressPersistence,
  crmOrgId,
  hideActionButtons = false,
  lastUserMessageAction,
}: AiChatMessagesProps) => {
  const [initialMsgCount, setInitialMsgCount] = useState(messages.length);
  const isInitialChatState = initialMsgCount === messages.length;
  const prevActiveChatId = usePrevious(activeChatId);

  // Build version map from messages for artifact preview boxes
  const artifactVersionMap = useMemo(() => buildArtifactVersionMap(messages), [messages]);

  const scrollToBottom = useCallback(
    (withScrollEffect?: boolean) => {
      const container = containerRef.current;
      const options: ScrollToOptions = { top: container?.scrollHeight };
      if (withScrollEffect) {
        options.behavior = 'smooth';
      }
      container?.scrollTo(options);
    },
    [containerRef],
  );

  const smoothlyScrollToBottom = useCallback(() => {
    scrollToBottom(true);
  }, [scrollToBottom]);

  //When an existing chat is clicked, it needs to be scrolled to the bottom (no need to show the animation)
  useEffect(() => {
    const container = containerRef.current;
    if (
      !container ||
      container.children.length === 0 ||
      messages.length === 0 ||
      !isInitialChatState
    )
      return;
    scrollToBottom();
  }, [containerRef, scrollToBottom, activeChatId, messages.length, isInitialChatState]);

  //This effect is to keep updated the "InitialMsgCount" number, when switching chats
  useEffect(() => {
    if (prevActiveChatId !== activeChatId) {
      setInitialMsgCount(messages.length);
    }
  }, [activeChatId, messages.length, prevActiveChatId]);

  // Calculate spacer height according to specific requirements
  const calculateSpacerHeight = useCallback(() => {
    // When viewing existing conversation (initial state), don't add spacer
    if (isInitialChatState) {
      return 0;
    }

    const positions = getElementPositionRelativeToScreenViewport(containerRef);
    const container = containerRef.current;
    if (!positions || !container) return 0;

    // Get visible container height
    const visibleContainerHeight =
      document.documentElement.clientHeight - positions.topFromTop - positions.bottomFromBottom;

    // Find and measure the last user message
    const allUserMessages = container.querySelectorAll('[data-message-type="user"]');
    const lastUserMessageElement = allUserMessages[allUserMessages.length - 1];
    const lastUserMessageHeight = lastUserMessageElement?.getBoundingClientRect().height || 0;

    // Find and measure the last assistant message
    const allAssistantMessages = container.querySelectorAll('[data-message-type="assistant"]');
    const lastAssistantMessageElement = allAssistantMessages[allAssistantMessages.length - 1];
    const lastAssistantMessageHeight =
      lastAssistantMessageElement?.getBoundingClientRect().height || 0;

    const lastResponseHeight = renderLoader ? LOADER_HEIGHT : lastAssistantMessageHeight;

    // Calculate total content height (all messages, not just last pair)
    const totalScrollHeight = container.scrollHeight;
    const currentSpacerElement = container.querySelector('[data-spacer]');
    const currentSpacerHeight = currentSpacerElement?.getBoundingClientRect().height || 0;
    const contentWithoutSpacer = totalScrollHeight - currentSpacerHeight;

    // Only add spacer if content would overflow the visible area
    // This prevents artificial scrollbar for short conversations
    if (contentWithoutSpacer <= visibleContainerHeight) {
      return 0;
    }

    const spacerHeight =
      visibleContainerHeight -
      lastUserMessageHeight -
      lastResponseHeight -
      GAP_BETWEEN_MESSAGES * VISIBLE_GAPS_COUNTS -
      (spacerFix ?? 0);

    return Math.max(0, spacerHeight);
  }, [isInitialChatState, containerRef, spacerFix, renderLoader]);

  // Use state to force re-calculation when content changes
  const [spacerHeight, setSpacerHeight] = useState(0);
  const messagesContentRef = useRef<HTMLDivElement>(null);
  const [contentResizeCount, setContentResizeCount] = useState(0);

  // Watch for content size changes (e.g., when reasoning section expands/collapses)
  useEffect(() => {
    const messagesContent = messagesContentRef.current;
    if (!messagesContent) return;

    const resizeObserver = new ResizeObserver(() => {
      setContentResizeCount((prev) => prev + 1);
    });

    resizeObserver.observe(messagesContent);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  // Recalculate spacer height when content changes
  useEffect(() => {
    const updateSpacerHeight = () => {
      const newHeight = calculateSpacerHeight();
      setSpacerHeight(newHeight);
    };

    // Recalculate after DOM updates
    const timeoutId = setTimeout(updateSpacerHeight, 0);

    return () => clearTimeout(timeoutId);
  }, [
    calculateSpacerHeight,
    messages.length,
    renderLoader,
    isStreamingResponse,
    smoothlyScrollToBottom,
    contentResizeCount,
  ]);

  useEffect(() => {
    if (renderLoader && spacerHeight) {
      smoothlyScrollToBottom();
    }
  }, [smoothlyScrollToBottom, renderLoader, spacerHeight]);

  return (
    <Stack ref={messagesContentRef} gap={GAP_BETWEEN_MESSAGES + 'px'}>
      {messages.map((message, idx) => {
        if (message.hidden) return null;
        const isLastMessage = idx === messages.length - 1;

        // Extract text content for highlighting (handles both string and structured content)
        const textContent = getTextContent(message.content);
        const highlightedTextContent = searchTermToHighlight
          ? highlightMatchInMarkdown(textContent, searchTermToHighlight)
          : textContent;

        switch (message.role) {
          case ChatCompletionRequestMessageRole.USER:
            const isLastUserMessage =
              isLastMessage ||
              !messages
                .slice(idx + 1)
                .some((m) => m.role === ChatCompletionRequestMessageRole.USER);
            return (
              <AiChatUserMessageBox
                key={`user_message_${idx}`}
                content={highlightedTextContent}
                onMount={smoothlyScrollToBottom}
                replyTo={message.replyTo}
                actionButton={isLastUserMessage ? lastUserMessageAction : undefined}
              />
            );
          case ChatCompletionRequestMessageRole.ASSISTANT:
            return (
              <AiChatAssistantMessageBox
                key={`assistant_message_${idx}_${activeChatId}`}
                message={message} // Keep original message with structured content
                searchTermToHighlight={searchTermToHighlight}
                onTextSelection={onTextSelection}
                isStreamingResponse={isStreamingResponse && isLastMessage}
                chatName={chatName}
                dataTableVariant={dataTableVariant}
                onAnimationProgressPersistence={onAnimationProgressPersistence}
                artifactVersionMap={artifactVersionMap}
                chatId={activeChatId}
                crmOrgId={crmOrgId}
                hideActionButtons={hideActionButtons}
              />
            );

          default:
            return <Fragment key={`no_message_${idx}`} />;
        }
      })}

      {errorMsg && (
        <AiChatAssistantMessageBoxSimple
          key="error-assistant"
          content={errorMsg}
          onMount={smoothlyScrollToBottom}
          errorState
        />
      )}

      {renderChatPaywallBlockMessage && (
        <AiChatAssistantMessageBoxSimple
          content={<AiChatPaywallBlockMessage />}
          onMount={smoothlyScrollToBottom}
        />
      )}

      {renderInitialFetchMessage && (
        <AiChatAssistantMessageBoxSimple
          content={<AiChatInitialFetchMessage />}
          onMount={smoothlyScrollToBottom}
        />
      )}

      {renderLoader && (
        <Box pl={1.5} width="fit-content">
          <CenteredCircularProgress size={16} color={colors.grey[800]} />
        </Box>
      )}

      {/* Spacer to align user message to container top after new message */}
      <div data-spacer style={{ height: `${spacerHeight}px` }} />
    </Stack>
  );
};
